import {
  ActionButton,
  BetaTag,
  GLTranslate,
  logFathomEvent,
  setLoginAnalytics,
  SVG,
  useGLTranslation,
  useLocalizedFetchOptions,
  useThemedStylesWithBreaks
} from 'notability-services-common-frontend'
import {FormEvent, useMemo, useState} from 'react'
import {config} from '../config'
import {useCreateMagicLinkMutation} from '../graphql/graphql'
import {useExperimentValue, useExperimentValues, useLoggedIn} from 'notability-services-next-frontend'

import {postJSON} from '../util/post'
import {useTranslateApolloError} from '../hooks/useTranslateApolloError'
import {loginStyles} from './LoginStyles'
import {SingleSignOn} from './SingleSignOn'
import {useIsWebAppUser} from '../hooks/useIsWebAppUser'
import {PolicyAndTermsReminder} from './PolicyAndTermsReminder'
import {type TranslationKey, redirectQueryParam, webAppAuthQueryParam} from 'notability-services-common-universal'
import {useCustomRouter} from '../util/useCustomRouter'

const authTypes = ['magic-link', 'username-and-password', 'sso-only'] as const
type AuthType = (typeof authTypes)[number]

export type FormState = 'before-submit' | 'submitting'
export type AuthFlowType = 'sign-in' | 'sign-up'

export interface LoginFormArgs {
  onLoggedIn: () => void
  errorKey?: TranslationKey
  messageKey?: TranslationKey
  titleKey?: TranslationKey
  defaultFormState?: FormState
  flowType?: AuthFlowType
}

export async function doesEmailExist(email: string): Promise<boolean | undefined> {
  try {
    const response = await fetch(`${config.API_SERVICE_URL}/auth/verify`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({email})
    })
    const {found} = await response.json()
    return found
  } catch (err) {
    console.error(err)
    return undefined
  }
}

export default function LoginForm(props: LoginFormArgs) {
  const {onLoggedIn, defaultFormState = 'before-submit', flowType = 'sign-in', errorKey, titleKey, messageKey} = props
  const {logIn} = useLoggedIn()
  const router = useCustomRouter()
  const experimentValues = useExperimentValues()

  const [authType, setAuthType] = useState<AuthType>('sso-only')
  const showBetaLabel = useExperimentValue<boolean>('showBetaLabel')

  const postOptions = useLocalizedFetchOptions()
  const {t} = useGLTranslation()

  const [errorMessage, setErrorMessage] = useState(errorKey ? t(errorKey) : '')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formState, setFormState] = useState<FormState>(defaultFormState)
  const [isFormPending, setIsFormPending] = useState(false)
  const [displayLogoutMessage, setMessageModalOpen] = useState(Boolean(messageKey && titleKey))
  const translateApolloError = useTranslateApolloError()

  const s = useThemedStylesWithBreaks(loginStyles)

  const isFormDisabled = formState === 'submitting' || isFormPending || email.length === 0
  const isFromWebApp = useIsWebAppUser()

  const clearSensitiveFields = () => {
    setEmail('')
    setPassword('')
  }

  const authSwitchText: TranslationKey = useMemo(() => {
    if (authType === 'magic-link') {
      return flowType === 'sign-in' ? 'signInWithPasswordInstead' : 'signUpWithPasswordInstead'
    } else {
      return flowType === 'sign-in' ? 'signInWithEmailInstead' : 'signUpWithEmailInstead'
    }
  }, [authType, flowType])

  const [createMagicLink] = useCreateMagicLinkMutation({
    onCompleted: (_data) => {
      setErrorMessage('')
      router.pushEmailLinkConfirmation(flowType)
    },
    onError: (err) => {
      setErrorMessage(translateApolloError(err).join(' '))
      setFormState('before-submit')
    }
  })

  // ensure login/signup attempts aren't spammed (add 1 second delay between attempts)
  const delayRequestAttempts = () => {
    setIsFormPending(true)
    setTimeout(() => setIsFormPending(false), 1_000)
  }

  /** Perform the login action upon pressing the 'login' button. */
  const doLogin = async (e: FormEvent) => {
    e.preventDefault()
    delayRequestAttempts()
    if (authType === 'magic-link') {
      const found = await doesEmailExist(email)
      setFormState('submitting')

      setLoginAnalytics({type: 'magic-link', isNewAccount: !found})

      createMagicLink({
        variables: {
          createMagicLinkParams: {
            email,
            signUp: !found,
            isFromWebApp
          }
        }
      })
    } else {
      try {
        setFormState('submitting')
        const {
          screenname: sn,
          userId,
          emailVerifiedAt
        } = await postJSON(config.API_SERVICE_URL, '/auth/login', postOptions, {
          email,
          password,
          isFromWebApp
        })

        setLoginAnalytics({type: 'password', isNewAccount: false})

        logIn(userId, sn, email, emailVerifiedAt, null, null)
        onLoggedIn()
        clearSensitiveFields()
      } catch (err: any) {
        setErrorMessage(err.message)
        setFormState('before-submit')
      }
    }

    return false
  }

  if (displayLogoutMessage && titleKey && messageKey) {
    return (
      <div style={s.formContainer}>
        <div style={s.loginMessageContainer}>
          <p style={s.loginMessageHeader}>{t(titleKey)}</p>
          <p style={s.loginMessage}>{t(messageKey)}</p>
          <div>
            <ActionButton
              text={t('OK')}
              appearance={'filled'}
              size={'medium'}
              onClick={() => setMessageModalOpen(false)}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={s.formContainer}>
      <div style={s.hStack}>
        {isFromWebApp && (
          <div style={s.betaHeader}>
            {showBetaLabel ? <BetaTag /> : null}
            <div>{t('notabilityWeb')}</div>
          </div>
        )}
        <span style={s.heading}>{t(flowType === 'sign-in' ? 'Sign in to Notability' : 'Sign up to get started')}</span>
        <span style={s.body}>{t('accountSignInOrCreate')}</span>
      </div>
      <SingleSignOn />
      <form style={s.form} onSubmit={doLogin}>
        <div style={s.hStack}>
          {authType !== 'sso-only' && (
            <>
              <p style={s.inputLabel}>{t('Email Address')}</p>
              <input
                style={s.input}
                autoFocus={true}
                autoComplete="email"
                type="email"
                value={email}
                onChange={(target) => setEmail(target.target.value)}
                placeholder={t('Email Address')}
                onFocus={(e) => (e.target.placeholder = '')}
                onBlur={(e) => (e.target.placeholder = t('Email Address'))}
                required={true}
              />
              {authType === 'username-and-password' && (
                <>
                  <input
                    style={s.input}
                    autoComplete={'current-password'}
                    onFocus={(e) => (e.target.placeholder = '')}
                    onBlur={(e) => (e.target.placeholder = t('Enter Password'))}
                    type="password"
                    value={password}
                    onChange={(target) => setPassword(target.target.value)}
                    placeholder={t('Enter Password')}
                    required={true}
                  />
                  <a
                    style={s.forgotPassword}
                    href={`${config.API_SERVICE_URL}/password/reset${
                      isFromWebApp ? `?${redirectQueryParam}=${webAppAuthQueryParam}` : ''
                    }`}
                  >
                    {t('Forgot password?')}
                  </a>
                </>
              )}
              <ActionButton
                size="small"
                appearance="filled"
                style={s.button}
                disabled={isFormDisabled}
                text={t(authType === 'magic-link' ? 'Continue' : formState === 'submitting' ? 'loggingIn' : 'Continue')}
                formSubmit={true}
                iconCreator={
                  formState === 'submitting'
                    ? () => (
                        <SVG.LoadingSmallFlexible
                          className="spin"
                          style={s.loadingSpinner}
                          {...s.loadingSpinnerColor}
                        />
                      )
                    : undefined
                }
              />
            </>
          )}
          {errorMessage && <p style={s.errorText}>{errorMessage}</p>}
          {(experimentValues.passwordSignupsEnabled || flowType === 'sign-in' || authType === 'sso-only') && (
            <span style={s.authTypeSwitch}>
              <GLTranslate i18nKey={authSwitchText}>
                <span
                  style={s.authTypeSwitchAction}
                  onClick={() => {
                    if (authType === 'magic-link') {
                      if (flowType === 'sign-up') {
                        router.pushCreatePassword(email)
                      } else {
                        logFathomEvent('Login - Switch to Password')
                        setAuthType('username-and-password')
                      }
                    } else {
                      logFathomEvent(flowType === 'sign-up' ? 'Signup - Switch to Email' : 'Login - Switch to Email')
                      setAuthType('magic-link')
                    }
                  }}
                />
              </GLTranslate>
            </span>
          )}
          {(authType === 'username-and-password' || flowType === 'sign-up') && (
            <span style={s.authTypeSwitch}>
              <GLTranslate i18nKey={flowType === 'sign-up' ? 'signUpSwitchToLogin' : 'signInSwitchToSignUp'}>
                <span
                  style={s.authTypeSwitchAction}
                  onClick={() => {
                    if (flowType === 'sign-up') {
                      router.pushLoginAbsoluteRoute()
                    } else {
                      router.pushSignup()
                    }
                  }}
                />
              </GLTranslate>
            </span>
          )}
        </div>
        <PolicyAndTermsReminder />
      </form>
    </div>
  )
}
